<template>
  <div>
        <!-- GLOBAL MATERIALS -->
        <div v-if="canApplyAssetMaterials">
          <div class="vform-label white">GLOBAL</div>
          <span class="lighter">{{ $t("globalMaterialPrompt") }}</span>
          <material-previewer-table
            v-if="assetValue"
            :key="forceReRenderKey"
            :value="assetValue"
            :show-save-button="false"
            :default-tiling="defaultTiling"
            :is-loading="isLoading"
            @request-materials="loadMaterials"
            ref="global"
            class="mb-4"
          />
        </div>

        <!-- LOCAL MATERIALS -->
        <div>
          <div class="vform-label white">
            LOCAL <span v-if="isMultiInstance">(Multiple)</span>
          </div>
          <span v-if="!isMultiInstance" class="lighter">
            {{ $t("localMaterialPrompt1") }}
          </span>
          <div v-else class="form-error p-1 mt-1 mb-1">
            {{ $t('localMaterialPrompt2') }}
          </div>

          <material-previewer-table
            v-if="instanceValue"
            :key="forceReRenderKey"
            :value="instanceValue"
            :show-save-button="false"
            :default-tiling="defaultTiling"
            :is-loading="isLoading"
            @request-materials="loadMaterials"
            ref="local"
            class="mb-1"
          />
        </div>

        <!-- Does the counter ever appear? I've never seen it. -->
        <loading-panel v-if="saving" />
        <div v-if="saving">{{ counter }} / {{ total }}</div>
        <loading-spinner v-if="isLoading" />
        <div
          v-if="!isLoading"
          class="settings-button switched-colors d-inline-block"
          @click="saveAll"
        >
          {{ $t("save") }}
        </div>
  </div>
</template>

<script>
import MaterialPreviewerTable from "@/components/widgets/editors/materialpreviewer/MaterialPreviewerTable.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import LoadingPanel from "@/components/LoadingPanel.vue";
import { sleep , updateCacheInstances} from "@/utils";
import {SpecialUuids} from "@/enum";
export default {
  name: "MobileMaterialSetter",
  components: {
    MaterialPreviewerTable,
    LoadingSpinner,
    LoadingPanel,
  },
  props: {
    instanceId: { type: String, required: true },
    multiInstanceIds: {type: Array, default: () => {return [];},},
    assetId: { type: String, required: true },
    projectId: { type: String, required: true },
    name: { type: String, default: "" },
    organizationId: { type: String, required: true },
    defaultTiling: {type: Array, default: () => {return [1, 1];},},
    /**
     * Type of the asset – possible values: node or model
     * */
    type: { type: String, required: true },
    isRootNode: { type: Boolean, default: false },
  },
  data() {
    return {
      listName: this.projectId + "-matlist",
      instanceValue: null,
      assetValue: null,
      forceReRenderKey: 0,
      saving: false,
      counter: 0,
      total: 0,
      isLoadingMaterialData: false,
    };
  },
  computed: {
    isMultiInstance() {
      return this.multiInstanceIds && this.multiInstanceIds.length > 0;
    },
    isLoading() {
      return this.$store.getters.isLoadingMaterials || this.isLoadingMaterialData;
    },
    canApplyAssetMaterials() {
      return this.type !== 'node' && !this.isMultiInstance;
    },
  },
  watch: {
    instanceId() {
      this.loadMaterials();
    },
    assetId() {
      this.loadMaterials();
    },
  },
  beforeMount() {
    this.init();
  },
  methods: {
    async init() {
      await this.$store.dispatch('initializeMaterials', {
        selectedOrganizationId: this.organizationId,
        listName: this.listName
      });
      await this.loadMaterials();
    },
    async saveInstanceValues() {
      this.counter = 0;
      const instanceVals = this.$refs.local.getValues();
      this.loading = true;
      const isMulti = this.multiInstanceIds && this.multiInstanceIds.length;
      if (isMulti) {
        await this.saveMultipleInstanceValues(instanceVals.length ? JSON.stringify(instanceVals) : null);
      } else {
        if (instanceVals.length) {
          await this.saveInstanceValue(JSON.stringify(instanceVals), this.instanceId);
        } else {
          await this.removeInstanceValue(this.instanceId);
        }
      }
    },
    async saveInstanceValue(value, instanceId) {
      return this.$store.dispatch('setInstanceMetaValues', {
        id: this.projectId,
        cid: instanceId,
        values: [{metaFieldId: SpecialUuids.MATERIAL_METAFIELD, value: value}]
      })
    },
    async removeInstanceValues() {
      this.saving = true;
      this.total = this.multiInstanceIds.length;
      for(let i = 0; i < this.multiInstanceIds.length; i++) {
        this.counter = i+1;
        await this.removeInstanceValue(this.multiInstanceIds[i]);
      }
      this.saving = false;
    },
    async removeInstanceValue(instanceId) {
      console.log('removing instance meta...')
      return this.$store.dispatch('clientUnsetInstanceMetaSetValues', {
        id: this.projectId,
        cid: instanceId,
        values: [SpecialUuids.MATERIAL_METAFIELD]
      })
    },
    async saveAssetValues() {
      const assetVals = this.$refs.global.getValues();
      this.loading = true;
      if(assetVals.length) {
        await this.saveAssetValue(JSON.stringify(assetVals));
      } else {
        await this.removeAssetValue();
      }
    },
    async saveAll() {
      const assetDirty = this.$refs.global ? this.$refs.global.dirty : false;
      const instanceDirty = this.$refs.local.dirty;
      console.log('saveAll called', assetDirty, instanceDirty);
      this.isLoadingMaterialData = true; // todo: muss das hier?

      if(assetDirty || instanceDirty) {
        if(assetDirty && this.$refs.global) {
          await this.saveAssetValues();
        }
        if(instanceDirty) {
          await this.saveInstanceValues();
        }

        this.$store.dispatch('createNotification', {'text': this.$t('events.metaValuesUpdated')});
        let payload = {
          projectId: this.projectId,
          assetIds: assetDirty ? [this.assetId] : [],
          instanceIds: instanceDirty ? (this.multiInstanceIds && this.multiInstanceIds.length ? this.multiInstanceIds : [this.instanceId]) : []
        };
        await updateCacheInstances(payload, this.$store);
        await sleep(500);
        await this.loadMaterials();
        this.loading = false;
        this.resetDirtyFlags();
      }

      this.isLoadingMaterialData = false;
    },
    resetDirtyFlags() {
      if(this.$refs.global) {
        this.$refs.global.resetDirty();
      }
      this.$refs.local.resetDirty();
    },
    // -- ASSET (GLOBAL) HANDLERS -- //
    async saveAssetValue(value) {
      return this.$store.dispatch("clientSaveAssetMetaSetValues", {
        id: this.assetId,
        values: [{
          metaFieldId: SpecialUuids.MATERIAL_METAFIELD,
          value: value
        }],
      });
    },
    async removeAssetValue() {
      return this.$store.dispatch("clientUnsetAssetMetaSetValues", {
        id: this.assetId,
        values: [SpecialUuids.MATERIAL_METAFIELD],
      });
    },
    // -- INSTANCE (LOCAL) HANDLERS -- //
    async saveMultipleInstanceValues(value) {
      this.saving = true;
      this.total = this.multiInstanceIds.length;
      for(let i = 0; i < this.total; i++) {
        this.counter = i+1;
        await this.saveInstanceValue(value, this.multiInstanceIds[i]);
      }
      this.saving = false;
    },
    async loadMaterials() {
      try {
        await this.$store.dispatch("loadAssemblyItemsInstances", {
            id: this.projectId,
            listName: this.listName,
            include: "squashedMeta",
            filter: `type in model node,id eq ${this.instanceId}`,
            limit: 1,
          }).then((res) => {
            if (res && res.length) {
              const instance = res[0];
              const meta = instance.squashedMetaFields;
              if (meta && meta.length) {
                const matVal = meta.filter((item) => {
                  return (
                    item.key === "material" &&
                    item.metaSetId === "03951580-26dd-4560-8541-272b584d78fc"
                  );
                });
                if (matVal && matVal.length) {
                  const {squashedMetaValues} = matVal[0];
                  this.instanceValue = squashedMetaValues.instanceMetaValue ? JSON.parse(squashedMetaValues.instanceMetaValue) : [];
                  this.assetValue = squashedMetaValues.assetMetaValue ? JSON.parse(squashedMetaValues.assetMetaValue) : [];
                }
              }
            }
          });
      } catch (e) {
        console.error(e);
      } finally {
        this.resetDirtyFlags();
        this.forceReRenderKey++;
      }
    },
  },
};
</script>
